import { useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth-context";
import { api, fmtCurrency } from "../../lib/api";
import { useI18n, LANGUAGES } from "../../lib/i18n";
import MobileHeader from "../../components/MobileHeader";
import CreditGauge from "../../components/CreditGauge";
import { useEffect, useState } from "react";
import { ChevronRight, User, Building2, Bell, ShieldCheck, HelpCircle, Gift, LogOut, FileText, Globe, Check, Sparkles } from "lucide-react";

const ITEMS = [
    { icon: User, label: "Personal Information" },
    { icon: Building2, label: "Bank Accounts" },
    { icon: Bell, label: "Notification Settings" },
    { icon: ShieldCheck, label: "Security" },
    { icon: HelpCircle, label: "Help & Support" },
    { icon: Gift, label: "Refer & Earn", chip: "Earn rewards" },
    { icon: FileText, label: "Documents" },
];

export default function ConsumerProfile() {
    const { user, logout } = useAuth();
    const { lang, setLang, t } = useI18n();
    const nav = useNavigate();
    const [score, setScore] = useState(null);
    const [langOpen, setLangOpen] = useState(false);

    useEffect(() => {
        api.get("/credit/score/raw").then((r) => setScore(r.data)).catch(() => {});
    }, []);

    if (!user) return null;
    const currentLang = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

    return (
        <div data-testid="consumer-profile">
            <MobileHeader title="My Profile" backTo="/consumer" variant="purple">
                <div className="flex flex-col items-center text-white pt-4">
                    {user.avatar_url && (
                        <img src={user.avatar_url} alt="" className="size-20 rounded-full ring-4 ring-white/30 object-cover" />
                    )}
                    <div className="font-display text-xl font-semibold mt-3">{user.full_name}</div>
                    <div className="text-xs text-white/80">{user.phone}</div>
                </div>
            </MobileHeader>

            <div className="px-5 -mt-4 pb-4 space-y-3">
                <div className="bg-white rounded-2xl border border-slate-200 p-5 product-shadow flex items-center gap-4" data-testid="profile-score-card">
                    <CreditGauge
                        score={score?.score || user.credit_score}
                        grade={score?.grade || "Very Good"}
                        size={120}
                        label=""
                    />
                    <div>
                        <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Credit DNA Score™</div>
                        <div className="text-xs text-slate-500 mt-2">Updated recently</div>
                        <button className="mt-2 text-xs font-bold text-[#0066FF]" data-testid="profile-view-factors">
                            See factors →
                        </button>
                    </div>
                </div>

                {/* KYC verification CTA */}
                <button
                    onClick={() => nav("/consumer/kyc")}
                    className="w-full bg-white rounded-2xl border border-slate-200/60 product-shadow p-4 flex items-center gap-3 tap-feedback"
                    data-testid="profile-kyc-cta"
                >
                    <div className={`size-11 rounded-xl flex items-center justify-center flex-shrink-0 ${user.kyc_status === "verified" ? "bg-emerald-50 text-emerald-700" : user.kyc_status === "submitted" ? "bg-amber-50 text-amber-700" : "bg-blue-50 text-[#0066FF]"}`}>
                        <ShieldCheck size={20} />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                        <div className="font-display font-semibold text-[#0A1930]">
                            {user.kyc_status === "verified" ? t("kyc.cta.verified") : user.kyc_status === "submitted" ? t("kyc.cta.submitted") : t("kyc.cta.pending")}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                            {user.kyc_status === "verified" ? t("kyc.cta.verified_sub") : user.kyc_status === "submitted" ? t("kyc.cta.submitted_sub") : t("kyc.cta.pending_sub")}
                        </div>
                    </div>
                    <ChevronRight size={16} className="text-slate-400" />
                </button>

                {/* Score Boosters CTA */}
                <button
                    onClick={() => nav("/consumer/boosters")}
                    className="w-full bg-white rounded-2xl border border-slate-200/60 product-shadow p-4 flex items-center gap-3 tap-feedback"
                    data-testid="profile-boosters-cta"
                >
                    <div className="size-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-[#7000FF] to-[#5d00d6] text-white">
                        <Sparkles size={20} />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                        <div className="font-display font-semibold text-[#0A1930]">{t("boosters.cta_title")}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{t("boosters.cta_sub")}</div>
                    </div>
                    <ChevronRight size={16} className="text-slate-400" />
                </button>

                <div className="bg-white rounded-2xl border border-slate-200/60 product-shadow overflow-hidden">
                    {ITEMS.map((it, i) => (
                        <button
                            key={it.label}
                            data-testid={`profile-item-${it.label.replace(/[^a-z]/gi, "")}`}
                            className={`w-full flex items-center gap-3 px-4 py-3.5 text-left tap-feedback ${i !== ITEMS.length - 1 ? "border-b border-slate-100" : ""}`}
                        >
                            <div className="size-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 flex-shrink-0">
                                <it.icon size={18} />
                            </div>
                            <span className="flex-1 text-sm font-medium">{it.label}</span>
                            {it.chip && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-bold">
                                    {it.chip}
                                </span>
                            )}
                            <ChevronRight size={16} className="text-slate-400" />
                        </button>
                    ))}
                </div>

                {/* Language switcher */}
                <div className="bg-white rounded-2xl border border-slate-200/60 product-shadow overflow-hidden">
                    <button
                        onClick={() => setLangOpen((o) => !o)}
                        className="w-full flex items-center gap-3 px-4 py-3.5 text-left tap-feedback"
                        data-testid="lang-switcher-btn"
                    >
                        <div className="size-9 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center flex-shrink-0">
                            <Globe size={18} />
                        </div>
                        <span className="flex-1 text-sm font-medium">{t("common.language")}</span>
                        <span className="text-xs font-bold text-slate-500">{currentLang.flag} {currentLang.label}</span>
                        <ChevronRight size={16} className={`text-slate-400 transition-transform ${langOpen ? "rotate-90" : ""}`} />
                    </button>
                    {langOpen && (
                        <div className="border-t border-slate-100">
                            {LANGUAGES.map((l) => (
                                <button
                                    key={l.code}
                                    onClick={() => { setLang(l.code); setLangOpen(false); }}
                                    className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-slate-50 ${l.code === lang ? "bg-violet-50 font-semibold" : ""}`}
                                    data-testid={`lang-option-${l.code}`}
                                >
                                    <span className="text-lg">{l.flag}</span>
                                    <span className="flex-1">{l.label}</span>
                                    {l.code === lang && <Check size={16} className="text-violet-600" />}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <button
                    onClick={() => { logout(); nav("/login"); }}
                    data-testid="profile-logout-btn"
                    className="w-full bg-white rounded-2xl border border-slate-200/60 px-4 py-3.5 flex items-center gap-3 text-rose-600 font-semibold tap-feedback"
                >
                    <div className="size-9 rounded-lg bg-rose-50 flex items-center justify-center">
                        <LogOut size={18} />
                    </div>
                    <span className="flex-1 text-left text-sm">Logout</span>
                    <ChevronRight size={16} className="text-rose-300" />
                </button>
            </div>
        </div>
    );
}
